import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getImageUrl } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { usePopup } from "contexts/PopupContext";
import KoachInfoPopup from "./KoachInfoPopup";
import StudentInfoPopup from "./StudentInfoPopup";
import CommentItem from "../../../common/v2/CommentItem";
import ReportPopup from "commons/popup/ReportPopup";
import LoadingComponent from "commons/loading/Loading.component";
import { getContentComments, createContentComment, deleteContentComment, toggleContentCommentLike, IContentComment } from "api/student/v2/content.api";
import { addComment, removeCommentById, updateCommentLike, findCommentById } from "helpers/Comment.helper";

interface ContentCommentPopupProps {
    contentId: number;
    contentTitle?: string;
    contentDesc?: string;
    onClose: () => void;
    onRefresh?: () => void;
}

const ContentCommentPopup: React.FC<ContentCommentPopupProps> = ({ 
    contentId, 
    contentTitle, 
    contentDesc,
    onClose,
    onRefresh 
}) => {
    const { info: userInfo } = useSelector((state: any) => state.user);
    const { showPopup } = usePopup();

    // 댓글 관련 상태
    const [commentText, setCommentText] = useState("");
    const [comments, setComments] = useState<any[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentLikeLoading, setCommentLikeLoading] = useState<{ [key: number]: boolean }>({});
    const [commentSubmitting, setCommentSubmitting] = useState(false);

    // 케밥 메뉴 관련 상태
    const [activeKebabMenu, setActiveKebabMenu] = useState<number | null>(null);
    const kebabRef = useRef<HTMLDivElement>(null);

    // 댓글 무한 스크롤을 위한 상태
    const [commentCurrentPage, setCommentCurrentPage] = useState(1);
    const [commentHasMore, setCommentHasMore] = useState(true);
    const [commentLoadingMore, setCommentLoadingMore] = useState(false);
    const [commentsPagination, setCommentsPagination] = useState({ currentPage: 1, hasNextPage: false });

    // 대댓글 관련 상태
    const [replyToComment, setReplyToComment] = useState<number | null>(null);
    const [replyText, setReplyText] = useState("");

    // 신고 관련 상태
    const [showReportPopup, setShowReportPopup] = useState(false);
    const [commentToReport, setCommentToReport] = useState<{ id: number; content: string } | null>(null);

    // 초기값을 visualViewport가 있으면 그것으로, 없으면 innerHeight로 설정
    const [viewportHeight, setViewportHeight] = useState(() => {
        if (typeof window !== 'undefined' && window.visualViewport) {
            return window.visualViewport.height;
        }
        return typeof window !== 'undefined' ? window.innerHeight : 0;
    });

    useEffect(() => {
        const handleResize = () => {
            if (window.visualViewport) {
                setViewportHeight(window.visualViewport.height);
            } else {
                setViewportHeight(window.innerHeight);
            }
        };

        handleResize();

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleResize);
            window.visualViewport.addEventListener('scroll', handleResize);
        }
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleResize);
                window.visualViewport.removeEventListener('scroll', handleResize);
            }
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    // 댓글 목록 데이터 가져오기
    const fetchCommentsData = useCallback(async (page: number, isLoadMore: boolean = false) => {
        if (!contentId) return;

        try {
            if (isLoadMore) {
                setCommentLoadingMore(true);
            } else {
                setCommentsLoading(true);
            }

            const result = await getContentComments(contentId, { page, limit: 20 });
            if (result && result.commentList) {
                if (isLoadMore) {
                    setComments(prev => [...prev, ...result.commentList]);
                } else {
                    setComments(result.commentList);
                }
                setCommentHasMore(result.pagination.hasNextPage);
                setCommentCurrentPage(page);
                setCommentsPagination(result.pagination);
            }

        } catch (error) {
            console.error("댓글 목록 조회 실패:", error);
        } finally {
            if (isLoadMore) {
                setCommentLoadingMore(false);
            } else {
                setCommentsLoading(false);
            }
        }
    }, [contentId]);

    useEffect(() => {
        if (contentId) {
            fetchCommentsData(1, false);
        }
    }, [contentId, fetchCommentsData]);

    // 통일된 사용자 클릭 핸들러
    const handleUserClick = (userId: string, userType: string) => {
        if (userType === 'student') {
            const popupContent = (
                <StudentInfoPopup
                    studentId={userId}
                    onClose={() => showPopup(null)}
                />
            );
            showPopup(popupContent);
        } else if (userType === 'koach') {
            const popupContent = (
                <KoachInfoPopup
                    koachId={userId}
                    onClose={() => showPopup(null)}
                />
            );
            showPopup(popupContent);
        }
    };

    // 케밥 메뉴 토글
    const toggleKebabMenu = (commentId: number) => {
        setActiveKebabMenu(activeKebabMenu === commentId ? null : commentId);
    };

    // 케밥 메뉴 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (kebabRef.current && !kebabRef.current.contains(event.target as Node)) {
                setActiveKebabMenu(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // 댓글 제출 핸들러
    const handleCommentSubmit = async () => {
        if (!commentText.trim() || !contentId) return;

        try {
            setCommentSubmitting(true);
            const result = await createContentComment(contentId, commentText.trim(), replyToComment || undefined);
            if (result && result.comment) {
                // 헬퍼 함수를 사용하여 댓글 추가
                setComments(prevComments => 
                    addComment(prevComments, result.comment, replyToComment || undefined)
                );

                // 스크롤 처리
                setTimeout(() => {
                    if (replyToComment) {
                        const commentElement = document.getElementById(`comment-${replyToComment}`);
                        if (commentElement) {
                            commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    } else {
                        const newCommentElement = document.getElementById(`comment-${result.comment.id}`);
                        if (newCommentElement) {
                            newCommentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }
                }, 100);

                setCommentText("");
                setReplyToComment(null);
                setReplyText("");
                
                // 콘텐츠 목록 새로고침 (댓글 수 업데이트)
                if (onRefresh) {
                    onRefresh();
                }
            }

        } catch (error) {
            console.error("댓글 작성 실패:", error);
        } finally {
            setCommentSubmitting(false);
        }
    };

    // 댓글 좋아요 토글
    const handleCommentLikeToggle = async (commentId: number) => {
        if (!contentId) return;

        try {
            setCommentLikeLoading(prev => ({ ...prev, [commentId]: true }));
            const result = await toggleContentCommentLike(contentId, commentId);
            
            // 헬퍼 함수를 사용하여 부모/자식 댓글 모두에서 해당 commentId 찾아서 업데이트
            setComments(prevComments => 
                updateCommentLike(prevComments, commentId, {
                    isLiked: result.isLiked,
                    likeCount: result.likeCount
                })
            );
        } catch (error) {
            console.error("댓글 좋아요 토글 실패:", error);
        } finally {
            setCommentLikeLoading(prev => ({ ...prev, [commentId]: false }));
        }
    };

    // 댓글 삭제
    const handleCommentDelete = async (commentId: number) => {
        if (!contentId) return;

        // 헬퍼 함수를 사용하여 부모/자식 댓글에서 해당 commentId 찾기
        const comment = findCommentById(comments, commentId);
        if (!comment) {
            console.error("삭제할 댓글을 찾을 수 없음:", commentId);
            return;
        }

        // 댓글 작성자인 경우에만 삭제 가능
        const isCommentAuthor = comment.userId === userInfo?.id;
        if (!isCommentAuthor) {
            alert("자신이 작성한 댓글만 삭제할 수 있습니다.");
            return;
        }

        const isConfirmed = window.confirm("정말로 이 댓글을 삭제하시겠습니까?");
        if (isConfirmed) {
            try {
                await deleteContentComment(contentId, commentId);
                
                // 헬퍼 함수를 사용하여 부모/자식 댓글에서 해당 commentId 제거
                setComments(prevComments => removeCommentById(prevComments, commentId));
                
                // 콘텐츠 목록 새로고침 (댓글 수 업데이트)
                if (onRefresh) {
                    onRefresh();
                }
            } catch (error) {
                console.error("댓글 삭제 실패:", error);
                alert("댓글 삭제에 실패했습니다. 다시 시도해주세요.");
            }
        }
    };

    // 대댓글 클릭 핸들러
    const handleReplyClick = (commentId: number) => {
        if (replyToComment === commentId) {
            setReplyToComment(null);
            setReplyText("");
        } else {
            setReplyToComment(commentId);
            setReplyText("");
        }
    };

    // 대댓글 취소 핸들러
    const handleCancelReply = () => {
        setReplyToComment(null);
        setReplyText("");
    };

    // 댓글 신고 핸들러
    const handleCommentReport = (commentId: number, commentContent: string) => {
        setCommentToReport({ id: commentId, content: commentContent });
        setShowReportPopup(true);
        setActiveKebabMenu(null);
    };

    // 대댓글 제출 핸들러
    const handleReplySubmit = async () => {
        if (!replyText.trim() || !replyToComment || !contentId) return;

        try {
            setCommentSubmitting(true);
            const result = await createContentComment(contentId, replyText.trim(), replyToComment);
            if (result && result.comment) {
                // 헬퍼 함수를 사용하여 대댓글 추가
                setComments(prevComments => 
                    addComment(prevComments, result.comment, replyToComment)
                );

                // 해당 부모 댓글 위치로 스크롤
                setTimeout(() => {
                    const commentElement = document.getElementById(`comment-${replyToComment}`);
                    if (commentElement) {
                        commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);

                setReplyToComment(null);
                setReplyText("");
                
                // 콘텐츠 목록 새로고침 (댓글 수 업데이트)
                if (onRefresh) {
                    onRefresh();
                }
            }

        } catch (error) {
            console.error("대댓글 작성 실패:", error);
        } finally {
            setCommentSubmitting(false);
        }
    };

    // 무한 스크롤 핸들러
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollTop + clientHeight >= scrollHeight - 100 && commentHasMore && !commentLoadingMore) {
            fetchCommentsData(commentCurrentPage + 1, true);
        }
    };

    const inputRef = useRef<HTMLInputElement>(null);
    const handleInputFocus = () => {
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'nearest'
                });
            }
        }, 300);
    };

    const commentListMaxHeight = viewportHeight - 280;

    return (
        <>
            <div 
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 w-screen top-0 left-0 p-4 !m-0"
                style={{ height: `${viewportHeight}px` }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div 
                    className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-auto flex flex-col"
                    style={{ maxHeight: `${viewportHeight - 32}px` }}
                >
                    <div className="flex items-center justify-between mb-4 flex-shrink-0">
                        <h3 className="text-lg font-bold text-gray-900">
                            {getLanguageValue(PAGE_TYPE.STUDENT, "commentPopup.txtTitle") || "댓글"}
                        </h3>
                        <button className="text-gray-400 hover:text-gray-600" onClick={onClose}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* 콘텐츠 정보 */}
                    {(contentTitle || contentDesc) && (
                        <div className="mb-4 bg-gray-100 rounded-lg p-4 flex-shrink-0">
                            {contentTitle && (
                                <p className="text-gray-900 font-medium mb-1"
                                    style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        lineHeight: '1.5'
                                    }}
                                    title={contentTitle}
                                >{contentTitle}</p>
                            )}
                            {contentDesc && (
                                <p className="text-gray-600 text-sm"
                                    style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        lineHeight: '1.5'
                                    }}
                                    title={contentDesc}
                                >{contentDesc}</p>
                            )}
                        </div>
                    )}

                    {/* 댓글 목록 */}
                    {commentsLoading ? (
                        <LoadingComponent />
                    ) : (
                        <>
                            <div 
                                className="overflow-y-auto mb-4 flex-1"
                                style={{ maxHeight: `${Math.max(commentListMaxHeight, 200)}px` }}
                                onScroll={handleScroll}
                            >
                                <div className="space-y-4">
                                    {comments.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                                            <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                            </svg>
                                            <p className="text-center">
                                                {getLanguageValue(PAGE_TYPE.STUDENT, "commentPopup.txtNoComments") || "아직 작성된 댓글이 없습니다."}
                                            </p>
                                        </div>
                                    ) : (
                                        comments.map((comment) => (
                                            <div key={comment.id} className="space-y-2">
                                                <div id={`comment-${comment.id}`}>
                                                    <CommentItem
                                                        comment={comment as any}
                                                        isChild={false}
                                                        onLikeToggle={handleCommentLikeToggle}
                                                        onReplyClick={handleReplyClick}
                                                        onDeleteClick={handleCommentDelete}
                                                        onReportClick={handleCommentReport}
                                                        isMyComment={(comment: any) => comment.userId === userInfo?.id}
                                                        commentLikeLoading={commentLikeLoading}
                                                        showKebabMenu={activeKebabMenu}
                                                        onKebabToggle={toggleKebabMenu}
                                                        kebabRef={kebabRef}
                                                        onUserClick={handleUserClick}
                                                    />
                                                </div>

                                                {/* 자식 댓글들 */}
                                                {comment.childList && comment.childList.length > 0 && (
                                                    <div className="space-y-2 ml-8">
                                                        {comment.childList.map((childComment: any) => (
                                                            <div key={childComment.id} id={`comment-${childComment.id}`}>
                                                                <CommentItem
                                                                    comment={childComment as any}
                                                                    isChild={true}
                                                                    onLikeToggle={handleCommentLikeToggle}
                                                                    onReplyClick={handleReplyClick}
                                                                    onDeleteClick={handleCommentDelete}
                                                                    onReportClick={handleCommentReport}
                                                                    isMyComment={(comment: any) => comment.userId === userInfo?.id}
                                                                    commentLikeLoading={commentLikeLoading}
                                                                    showKebabMenu={activeKebabMenu}
                                                                    onKebabToggle={toggleKebabMenu}
                                                                    kebabRef={kebabRef}
                                                                    onUserClick={handleUserClick}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* 무한 스크롤 로딩 인디케이터 */}
                                {commentLoadingMore && (
                                    <div className="flex justify-center items-center py-4">
                                        <div className="flex items-center space-x-2">
                                            <svg className="w-4 h-4 animate-spin text-[#CF3688]" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 댓글 입력 */}
                            <div className="border-t pt-4 flex-shrink-0">
                                {/* 대댓글 표시 영역 */}
                                {replyToComment && (
                                    <div className="mb-2 p-2 bg-gray-50 rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <img src="/imgs/icons/icon-reply.svg" alt="대댓글" className="w-4 h-4 text-gray-500" />
                                                <span className="text-sm text-gray-600 truncate max-w-[200px]">
                                                    {comments.find((c: any) => c.id === replyToComment)?.text || ""}
                                                </span>
                                            </div>
                                            <button
                                                onClick={handleCancelReply}
                                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-2">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        className="flex-1 min-w-0 px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg
                                        focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688]"
                                        placeholder={replyToComment
                                            ? "대댓글을 입력하세요..."
                                            : (getLanguageValue(PAGE_TYPE.STUDENT, "commentPopup.txtCommentPlaceholder") || "댓글을 입력하세요...")
                                        }
                                        value={replyToComment ? replyText : commentText}
                                        onChange={(e) => replyToComment ? setReplyText(e.target.value) : setCommentText(e.target.value)}
                                        onFocus={handleInputFocus}
                                        maxLength={200}
                                        disabled={commentSubmitting}
                                    />
                                    <button
                                        onClick={replyToComment ? handleReplySubmit : handleCommentSubmit}
                                        disabled={(replyToComment ? !replyText.trim() : !commentText.trim()) || commentSubmitting}
                                        className="flex-shrink-0 px-4 sm:px-6 py-2 text-sm bg-[#CF3688] text-white rounded-lg hover:bg-[#CF3688]/90 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {replyToComment ? "답글" : (getLanguageValue(PAGE_TYPE.STUDENT, "commentPopup.btnWrite") || "입력")}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* 댓글 신고 팝업 - TODO: 콘텐츠 댓글 신고 기능 구현 필요 */}
            {showReportPopup && commentToReport && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                        <h3 className="text-lg font-bold mb-4">댓글 신고</h3>
                        <p className="text-gray-600 mb-4">콘텐츠 댓글 신고 기능은 아직 구현 중입니다.</p>
                        <button
                            onClick={() => {
                                setShowReportPopup(false);
                                setCommentToReport(null);
                            }}
                            className="w-full px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#CF3688]/90"
                        >
                            닫기
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ContentCommentPopup;
